import React, { useState, useEffect, useRef } from "react";
import { post } from "../../../services/api";

const FaceRegistrationModal = ({ isOpen, onClose, selectedStudent, rollNoProp }) => {
  const [rollNo, setRollNo] = useState("");
  
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const POSES = ['Front', 'Left', 'Right', 'Up', 'Down'];
  const [embeddings, setEmbeddings] = useState([null, null, null, null, null]);
  const [capturedImages, setCapturedImages] = useState([null, null, null, null, null]);
  const [status, setStatus] = useState({ msg: "", type: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false);

  // Initialize roll no
  useEffect(() => {
    if (isOpen) {
      if (rollNoProp) {
        setRollNo(rollNoProp);
      } else if (selectedStudent && selectedStudent.code) {
        setRollNo(selectedStudent.code);
      } else {
        setRollNo("");
      }
      
      // Clear previous states
      setEmbeddings([null, null, null, null, null]);
      setCapturedImages([null, null, null, null, null]);
      setStatus({ msg: "", type: "" });
    }
  }, [isOpen, selectedStudent, rollNoProp]);

  // Stop camera when modal closes or unmounts
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }
    return () => stopCamera();
  }, [isOpen, stream]); // Added stream dependency to capture latest state

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
      setCameraStarted(false);
    }
  };

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);


  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(s);
      setCameraStarted(true);
      setStatus({ msg: "Click a pose box to capture", type: "" });
    } catch {
      setStatus({ msg: "Cannot access webcam", type: "err" });
    }
  };

  const capturePose = async (idx) => {
    if (!stream || !videoRef.current) {
      await startCamera();
      // Need a slight delay to allow video to start playing before capture
      setTimeout(() => executeCapture(idx), 1000);
      return;
    }
    executeCapture(idx);
  };

  const executeCapture = (idx) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!video || !canvas || video.videoWidth === 0) {
      setStatus({ msg: "Camera not ready. Try again.", type: "err" });
      return;
    }
    
    canvas.width = video.videoWidth || 320;
    canvas.height = video.videoHeight || 240;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    setStatus({ msg: "Extracting face...", type: "" });
    
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setStatus({ msg: "Failed to capture image", type: "err" });
        return;
      }
      
      const fd = new FormData();
      fd.append("image", blob, "snap.jpg");
      
      try {
        const res = await post("https://presenceiq.inspirertechnologies.com/api/embed", fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        if (!res || res.error) {
          setStatus({ msg: res?.error || "No face detected — look at camera", type: "err" });
          return;
        }
        
        const newEmbeddings = [...embeddings];
        newEmbeddings[idx] = res.embedding;
        setEmbeddings(newEmbeddings);
        
        const newCapturedImages = [...capturedImages];
        newCapturedImages[idx] = canvas.toDataURL("image/jpeg", 0.7);
        setCapturedImages(newCapturedImages);
        
        const done = newEmbeddings.filter(Boolean).length;
        setStatus({
          msg: `${done} / 5 poses captured`,
          type: done === 5 ? "ok" : ""
        });
      } catch (err) {
        setStatus({ msg: "Failed to extract face", type: "err" });
      }
    }, "image/jpeg", 0.9);
  };

  const resetPoses = () => {
    setEmbeddings([null, null, null, null, null]);
    setCapturedImages([null, null, null, null, null]);
    setStatus({ msg: "", type: "" });
    stopCamera();
    // Re-start camera immediately for better UX
    setTimeout(() => startCamera(), 100);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const saveStudent = async () => {
    const roll = rollNo.trim();
    const embs = embeddings.filter(Boolean);
    
    if (!roll || embs.length === 0) {
      setStatus({ msg: "Student roll number is missing or no poses captured", type: "err" });
      return;
    }
    
    setStatus({ msg: "Saving...", type: "" });
    setIsSaving(true);
    
    try {
      const res = await post("https://presenceiq.inspirertechnologies.com/api/persons", { rollNo: roll, embeddings: embs });
      
      if (!res || res.error) {
        setStatus({ msg: res?.error || "Save failed", type: "err" });
      } else {
        setStatus({ msg: `✔ ${res.name || "Student"} registered successfully!`, type: "ok" });
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setStatus({ msg: "Save failed", type: "err" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const validEmbeddingsCount = embeddings.filter(Boolean).length;
  // canSave: At least 1 valid pose and rollNo exists
  const canSave = validEmbeddingsCount > 0 && rollNo.trim() !== "";

  return (
    <>
      <div 
        className="modal-backdrop fade show" 
        style={{ zIndex: 1060, backgroundColor: 'rgba(0,0,0,0.6)' }} 
        onClick={handleClose}
      ></div>
      <div 
        className="modal fade show d-block" 
        tabIndex="-1" 
        style={{ zIndex: 1065, overflowY: 'auto' }}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px' }}>
          <div className="modal-content shadow-lg border-0" style={{ borderRadius: '16px' }}>
            {/* Header */}
            <div className="modal-header border-bottom-0 pb-1 px-4 pt-4">
              <h5 className="modal-title fw-bold text-dark w-100 text-center position-relative">
                Attendance Registration
                <button 
                  type="button" 
                  className="btn-close position-absolute end-0 top-50 translate-middle-y" 
                  onClick={handleClose}
                  style={{ fontSize: '0.8rem' }}
                ></button>
              </h5>
            </div>
            
            <div className="modal-body px-4 pt-2 pb-4">


              {/* Pose Grid */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="text-center fw-semibold text-muted small mb-2">
                    Catch 5 Poses (Min. 1 Required)
                  </div>
                  <div className="pose-grid custom-pose-grid">
                    {POSES.map((pose, idx) => {
                      const isDone = Boolean(capturedImages[idx]);
                      return (
                        <div
                          key={pose}
                          className={`pose-box ${isDone ? "done border-success bg-white" : "border-secondary bg-light"}`}
                          onClick={() => capturePose(idx)}
                          title={`Click to capture ${pose} pose`}
                        >
                          {isDone && (
                            <img src={capturedImages[idx]} alt={pose} className="pose-img" />
                          )}
                          <div className={`pose-text ${isDone ? 'done-text' : 'muted-text'}`}>
                            {pose} {isDone && <i className="fa fa-check ms-1 text-success"></i>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Camera Area */}
              <div className="row mb-1">
                <div className="col-12 position-relative d-flex justify-content-center">
                  <div className="video-container bg-dark position-relative w-100 overflow-hidden d-flex align-items-center justify-content-center">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`reg-video ${cameraStarted ? "d-block" : "d-none"}`}
                    ></video>
                    
                    {!cameraStarted && (
                      <div className="text-center text-white-50 position-absolute" style={{ zIndex: 2 }}>
                        <i className="fa fa-camera fa-2x mb-2 d-block"></i>
                        <span className="small">Camera is off</span>
                      </div>
                    )}
                    
                    {/* Corner guidelines overlay for better UX */}
                    {cameraStarted && (
                      <div className="camera-overlay position-absolute top-0 start-0 w-100 h-100 pointer-events-none">
                        <div className="corner-tl"></div>
                        <div className="corner-tr"></div>
                        <div className="corner-bl"></div>
                        <div className="corner-br"></div>
                      </div>
                    )}
                  </div>
                  <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
                </div>
              </div>

              {/* Status Message */}
              <div className="row mt-2" style={{ minHeight: '30px' }}>
                <div className="col-12 text-center">
                  {status.msg && (
                    <div
                      className={`fw-medium small py-1 px-3 rounded-pill d-inline-block ${
                        status.type === "err" ? "text-danger bg-danger-subtle" : 
                        status.type === "ok" ? "text-success bg-success-subtle" : 
                        "text-primary bg-primary-subtle"
                      }`}
                    >
                      {status.type === "err" && <i className="fa fa-info-circle me-1"></i>}
                      {status.type === "ok" && <i className="fa fa-check-circle me-1"></i>}
                      {status.msg}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer / Actions */}
            <div className="modal-footer border-top-0 px-4 pb-4 pt-0 d-flex flex-column gap-2">
              <div className="d-flex w-100 gap-2">
                <button
                  className={`btn ${cameraStarted ? "btn-outline-primary" : "btn-primary"} flex-grow-1 py-2 fw-semibold rounded-pill`}
                  onClick={startCamera}
                  style={{ flex: 1 }}
                >
                  <i className={`fa ${cameraStarted ? 'fa-refresh' : 'fa-camera'} me-2`}></i>
                  {cameraStarted ? "Restart Camera" : "Start Camera"}
                </button>
                <button 
                  className="btn btn-outline-danger py-2 fw-semibold rounded-pill" 
                  onClick={resetPoses}
                  style={{ flex: 0.8 }}
                >
                  <i className="fa fa-times me-2"></i> Reset
                </button>
              </div>
              <button
                className="btn btn-success w-100 py-2 text-white rounded-pill shadow-sm"
                onClick={saveStudent}
                disabled={!canSave || isSaving}
                style={{ fontSize: '1.05rem' }}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-2"></i> Save Face Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-pose-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 10px;
        }
        .pose-box {
          aspect-ratio: 1;
          border-radius: 12px;
          border: 2px dashed #ced4da;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: all 0.2s ease;
        }
        .pose-box:hover {
          border-color: #0d6efd;
          background-color: #f8f9fa !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }
        .pose-box.done {
          border-style: solid;
          border-color: #198754;
        }
        .pose-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1;
        }
        .pose-text {
          font-size: 11px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 4px;
          z-index: 2;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .muted-text {
          color: #6c757d;
        }
        .done-text {
          color: #198754;
          background: rgba(255, 255, 255, 0.9);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .video-container {
          height: 280px;
          border-radius: 12px;
          border: 1px solid #dee2e6;
        }
        .reg-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 1;
        }
        .pointer-events-none {
          pointer-events: none;
        }
        
        /* Camera overlay corners */
        .corner-tl, .corner-tr, .corner-bl, .corner-br {
          position: absolute;
          width: 30px;
          height: 30px;
          border-color: rgba(255, 255, 255, 0.7);
          border-style: solid;
        }
        .corner-tl { top: 15%; left: 15%; border-width: 3px 0 0 3px; }
        .corner-tr { top: 15%; right: 15%; border-width: 3px 3px 0 0; }
        .corner-bl { bottom: 15%; left: 15%; border-width: 0 0 3px 3px; }
        .corner-br { bottom: 15%; right: 15%; border-width: 0 3px 3px 0; }
        
        @media (max-width: 576px) {
          .custom-pose-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
          }
          .video-container {
            height: 220px;
          }
        }
      `}</style>
    </>
  );
};

export default FaceRegistrationModal;
